import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Infinity as InfinityIcon, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { HEYCATCH_EVENTS, trackOutcomeOnce } from "@/lib/heycatch";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog, ReportDialog } from "@/components/safety/Dialogs";
import ConversationList from "@/components/messages/ConversationList";
import MessageThread from "@/components/messages/MessageThread";
import { toast } from "sonner";

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh, openPaywall } = useAuth();
  const activeId = params.get("c");

  const [list, setList] = useState({ conversations: [], total_unread: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const bottomRef = useRef(null);

  const loadList = useCallback(async ({ silent = false } = {}) => {
    try {
      const { data } = await api.get("/conversations");
      setList(data);
    } catch (e) {
      // Background refreshes must not interrupt a conversation with a toast.
      devWarn("Messages.loadList", e);
      if (!silent) toast.error(apiError(e));
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(
    async (id, { silent = false } = {}) => {
      if (!id) {
        setThread(null);
        setMessages([]);
        return;
      }
      // Only the first load (or an explicit reload) shows the spinner - the 15s
      // background poll used to flip the thread back into its loading state.
      if (!silent) setLoadingThread(true);
      try {
        const { data } = await api.get(`/conversations/${id}/messages`);
        setThread(data.conversation);
        setMessages(data.messages);
      } catch (e) {
        devWarn("Messages.loadThread", e);
        if (!silent) {
          toast.error(apiError(e, "Conversation not found."));
          setParams({});
        }
      } finally {
        if (!silent) setLoadingThread(false);
      }
    },
    [setParams]
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadThread(activeId);
  }, [activeId, loadThread]);

  // Single stable polling interval. `activeId` is read through a ref so the
  // interval is not torn down and recreated every time the open conversation
  // changes, and polling pauses while the tab is hidden.
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      loadList({ silent: true });
      const current = activeIdRef.current;
      if (current) loadThread(current, { silent: true });
    }, 15000);
    return () => clearInterval(t);
  }, [loadList, loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const hasUnlimited = user?.has_unlimited;
  const remaining = user?.free_messages_remaining ?? 0;

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, {
        message_text: text.trim(),
      });
      setMessages((m) => [...m, data.message]);
      setText("");
      await refresh();
      // HeyCatch: the member's first ever sent message - the moment a signup
      // turns into a real conversation. Guarded per user id so it reports once
      // and never again. Message content is NEVER forwarded; the event carries
      // no properties at all.
      trackOutcomeOnce(
        `first_message:${user?.id}`,
        HEYCATCH_EVENTS.FIRST_MESSAGE_SENT
      );
      if (data.low_balance_warning) {
        toast.warning(
          `You have ${data.free_messages_remaining} free ${
            data.free_messages_remaining === 1 ? "message" : "messages"
          } remaining.`
        );
      }
      loadList();
    } catch (err) {
      if (err?.response?.status === 402) {
        openPaywall(err.response.data.detail);
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setSending(false);
    }
  };

  const other = thread?.other_user;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">Messages</h1>
        <div
          data-testid="message-counter-banner"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold ${
            hasUnlimited
              ? "bg-[#E4F3EA] text-[#1F7A4D] border border-[#B7DFC7]"
              : remaining <= 5
              ? "bg-[#FBF0DC] text-[#8A5A12] border border-[#E7CF9E]"
              : "bg-[#D7EEF0] text-[#1F6F78] border border-[#BFE0E3]"
          }`}
        >
          {hasUnlimited ? <InfinityIcon className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
          {user?.access_label}
          {!hasUnlimited && (
            <button
              onClick={() => navigate("/pricing")}
              data-testid="upgrade-from-banner"
              className="ml-1 underline"
            >
              Upgrade
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-[320px_1fr] gap-5">
        {/* Conversation list */}
        <ConversationList
          activeId={activeId}
          list={list}
          loadingList={loadingList}
          navigate={navigate}
          setParams={setParams}
        />

        {/* Thread */}
        <MessageThread
          activeId={activeId}
          bottomRef={bottomRef}
          loadingThread={loadingThread}
          messages={messages}
          navigate={navigate}
          other={other}
          sending={sending}
          send={send}
          setBlockOpen={setBlockOpen}
          setParams={setParams}
          setReportOpen={setReportOpen}
          setText={setText}
          text={text}
          thread={thread}
          user={user}
          hasUnlimited={hasUnlimited}
        />
      </div>

      {other && (
        <>
          <ReportDialog
            open={reportOpen}
            reportedUser={other}
            conversationId={activeId}
            onClose={() => setReportOpen(false)}
          />
          <ConfirmDialog
            open={blockOpen}
            danger
            title={`Block ${other.display_name}?`}
            description="They will no longer be able to message you, and any sponsorship connection between you will end. Blocking is always free."
            confirmLabel="Block member"
            onClose={() => setBlockOpen(false)}
            onConfirm={async () => {
              try {
                await api.post(`/blocks/${other.id}`);
                toast.success("Member blocked.");
                setBlockOpen(false);
                setParams({});
                loadList();
              } catch (e) {
                toast.error(apiError(e));
              }
            }}
          />
        </>
      )}
    </div>
  );
}
